/**
 * Companies list page - thin wrapper around generic CRMObjectListPage.
 */

import { MessageChatCircle } from '@untitledui/icons';
import { useMemo } from 'react';

import { useUser } from '@/allauth_auth/hooks';
import { useOrg } from '@/contexts/OrgContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import type {
  BulkAction,
  ListPageConfig,
  RowAction,
} from '@/lib/crm/core/types';
import { useCreateDeal } from '@/lib/crm/hooks/useCreateDeal';
import { companyListConfig, CRMObjectListPage } from '@/lib/crm/list';
import { useChatAboutRecords } from '@/lib/crm/list/hooks/useChatAboutRecords';
import { companyService } from '@/lib/crm/services';
import { getEntityTypeIconComponent } from '@/lib/crm/utils/icons/EntityTypeIcon';
import { useViewHealthMarker } from '@/lib/view-health';
import type { CompanyCreateData } from '@/types/crm';

const DealIcon = getEntityTypeIconComponent('ahoy_deal');

export default function CompaniesList() {
  const user = useUser();
  const { currentOrg } = useOrg();
  useViewHealthMarker('companies-list-ready', { ready: true });
  const chatAboutRecords = useChatAboutRecords();
  const isChatAboutEnabled = useFeatureFlag('chat_about_records');

  const { createDealForCompany } = useCreateDeal(currentOrg?.id ?? 0);

  const config = useMemo((): ListPageConfig => {
    const bulkChatAction: BulkAction = {
      id: 'chat-about-records',
      label: 'Chat about these records',
      icon: MessageChatCircle,
      priority: 'primary',
      requires: { feature: 'chat' },
      onClick: chatAboutRecords.onClick,
      onClickWithFilters: chatAboutRecords.onClickWithFilters,
    };

    const rowCreateDealAction: RowAction = {
      id: 'create-deal',
      label: 'Create deal',
      icon: DealIcon,
      requires: { permission: ['ahoy_deal', 'ahoy_object_created'] },
      onClick: object => createDealForCompany(object),
    };

    return {
      ...companyListConfig,
      bulkActions: [
        ...(companyListConfig.bulkActions ?? []),
        ...(isChatAboutEnabled ? [bulkChatAction] : []),
      ],
      rowActions: [
        ...(companyListConfig.rowActions ?? []),
        rowCreateDealAction,
      ],
    };
  }, [chatAboutRecords, isChatAboutEnabled, createDealForCompany]);

  return (
    <CRMObjectListPage
      config={config}
      onCreateObject={async (orgId, data) => {
        if (!user?.id) {
          throw new Error('User not authenticated');
        }
        await companyService.create(
          orgId,
          data as unknown as CompanyCreateData,
          user.id
        );
      }}
    />
  );
}
